import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { AreaOfResponsibility } from '../types/aor';
import { selectGroupGuid, useAppSelector } from '../slices/commonSelectors';
import { toast } from 'react-toastify';
import { toastConfig, noAutoClose } from 'assets/data/config';
import { ToastOptions } from 'react-toastify';

export const useAors = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  return useQuery({
    queryKey: ['aors', groupGuid],
    queryFn: async () => {
      const response = await axios.post<AreaOfResponsibility[]>(
        config.apiGateway.getAllAor,
        {
          group_guid: groupGuid,
        }
      );

      // Parse the location and current_location_geom fields
      return response.data.map((aor) => {
        let geolocation = null;
        try {
          if (aor.location) {
            // Try to parse the location string into a GeoLocation object
            const parsedLocation =
              typeof aor.location === 'string'
                ? JSON.parse(aor.location)
                : aor.location;
            geolocation = {
              ...parsedLocation,
              geojson: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: [
                    {
                      type: 'Feature',
                      geometry:
                        typeof aor.current_location_geom === 'string'
                          ? JSON.parse(aor.current_location_geom)
                          : aor.current_location_geom,
                    },
                  ],
                },
              },
            };
          }
        } catch (error) {
          console.error('Error parsing location data for AOR:', aor.id, error);
        }

        return {
          ...aor,
          geolocation,
        };
      });
    },
    enabled: Boolean(groupGuid),
  });
};

export const useCreateAor = () => {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);

  return useMutation({
    mutationFn: async (aor: AreaOfResponsibility) => {
      const response = await axios.post(config.apiGateway.upsertAor, {
        aor,
        group_guid: groupGuid,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aors', groupGuid] });
    },
  });
};

export const useUpdateAor = () => {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);

  return useMutation({
    mutationFn: async (aor: AreaOfResponsibility) => {
      const response = await axios.post(config.apiGateway.upsertAor, {
        aor,
        group_guid: groupGuid,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aors', groupGuid] });
    },
  });
};

export const useDeleteAor = () => {
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post(config.apiGateway.deleteAor, {
        aor_id: id,
        group_guid: groupGuid,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aors', groupGuid] });
      toast.success('AOR deleted successfully', toastConfig);
    },
    onError: (error: any) => {
      if (error.response?.data?.errorType === 'AOR_IN_USE') {
        toast.error(
          error.response.data.message ||
            'This AOR is in use by an Alert Configuration and cannot be deleted until the Alert Configuration is deleted or updated to a different AOR.',
          noAutoClose as ToastOptions
        );
      } else {
        toast.error('Failed to delete AOR. Please try again.', toastConfig);
      }
    },
  });
};
